import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout';
import { showToast } from '@/components/Layout/Toast';
import Alert from '@/components/UI/Alert';
import Card from '@/components/UI/Card';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import DataTable from '@/components/UI/DataTable';
import TradeHeader from '@/components/Trade/TradeHeader';
import TradeChart from '@/components/Chart/TradeChart';
import { renderTransactionOutput } from '@/components/Trade/TradeRenderUtils';
import { trimDecimals, getDatePlusMinus } from '@einhaus/meteoric-helpers';
import { trpc } from '@/api/trpc';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingRequisites } from '@/hooks/useTradingRequisites';
import TradeLogicV2ExitSummary, { type TradeLogicV2ExitDisplaySummary } from '@/components/TradeLogic/TradeLogicV2ExitSummary';
import type {
    TradingStrategyBacktestTrade,
    BarOHLCVDetailed,
    BarDuration,
    OptionBuyingStrategy,
    EntityOptionObjectCompressed
} from '@einhaus/project314-types';
import { useFetchOptions } from '@/hooks/useFetchOptions.tsx';
import { useFetchEntities } from '@/hooks/useFetchEntities.tsx';

// Convert BacktestTrade to be compatible with UserPortfolioTrade for the TradeChart and TradeHeader components
export type BacktestTrade = TradingStrategyBacktestTrade & {
    // Additional fields needed for UserPortfolioTrade compatibility
    strategyId: number;
    insertDateTime: string;
    exitCandidateId: number | null;
    exitCandidateHash: string | null;
    exitCandidateDisplay: TradeLogicV2ExitDisplaySummary | null;
};

const PortfolioBacktestTrades = () => {
    const [searchParams] = useSearchParams();
    const portfolioBacktestId = Number(searchParams.get('id') || '0');
    const entityId = Number(searchParams.get('entityId') || '0');
    const entityType = searchParams.get('entityType') || '';
    const startDateTime = searchParams.get('startDateTime') || '';
    const endDateTime = searchParams.get('endDateTime') || '';

    // Auth state is handled by AdminLayout
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [chartError, setChartError] = useState<string | null>(null);
    const tradesPerPage = 10;

    // Fetch backtest details
    const backtestQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioBacktest.queryOptions({ backtestId: portfolioBacktestId }),
            enabled: portfolioBacktestId > 0
        },
        {
            fallbackErrorMessage: 'Failed to fetch portfolio backtest',
            setError: (msg) => setError(msg)
        }
    );

    // Extract backtest data
    const backtest = backtestQuery.data?.backtest || null;

    // Fetch portfolio details
    const portfolioQuery = useAppQuery({
        ...trpc.user.fetchPortfolio.queryOptions({
            id: backtest?.portfolioId || 0,
            shouldFetchTrades: false
        }),
        enabled: !!backtest?.portfolioId
    });

    // Extract portfolio data
    const portfolio = useMemo(
        () => (portfolioQuery.data?.portfolios && portfolioQuery.data.portfolios.length > 0 ? portfolioQuery.data.portfolios[0] : null),
        [portfolioQuery.data]
    );

    // Fetch trades
    const tradesQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioBacktestTrades.queryOptions({ portfolioBacktestId }),
            enabled: portfolioBacktestId > 0
        },
        {
            fallbackErrorMessage: 'Failed to fetch portfolio backtest trades',
            setError: (msg) => setError(msg)
        }
    );

    // Process and filter trades
    const filteredTrades = useMemo(() => {
        if (!tradesQuery.data?.trades) return [];

        // Convert the trades to the format expected by our components
        const convertedTrades: BacktestTrade[] = tradesQuery.data.trades.map((trade) => ({
            ...trade,
            direction: trade.direction,
            strategyId: trade.strategyId,
            insertDateTime: trade.dateTime
        }));

        // Filter trades based on URL parameters
        return convertedTrades.filter((trade) => {
            if (entityId && trade.entityId !== entityId) return false;
            if (entityType && trade.entityType !== entityType) return false;

            // Filter by date if provided
            if (startDateTime && trade.transactions?.[0]) {
                const tradeTimestamp = new Date(trade.transactions[0].timestamp * 1000).getTime();
                const startTimestamp = new Date(startDateTime).getTime();
                if (tradeTimestamp < startTimestamp) return false;
            }

            if (endDateTime && trade.transactions?.[0]) {
                const tradeTimestamp = new Date(trade.transactions[0].timestamp * 1000).getTime();
                const endTimestamp = new Date(endDateTime).getTime();
                if (tradeTimestamp > endTimestamp) return false;
            }

            return true;
        });
    }, [tradesQuery.data, entityId, entityType, startDateTime, endDateTime]);

    const totalPages = Math.max(1, Math.ceil(filteredTrades.length / tradesPerPage));

    // Extract entities to fetch
    const entitiesToFetch = useMemo(() => {
        if (!filteredTrades.length) return [];

        // Use a Map to ensure uniqueness based on entity ID and type
        const entitiesMap = new Map<
            string,
            {
                // Id
                i: number;
                // Type
                t: 'a' | 'c' | 'i';
            }
        >();

        filteredTrades.forEach((trade) => {
            // Create a unique key for each entity
            const entityKey = `${trade.entityId}_${trade.entityType}`;

            // Only add if not already in the map
            if (!entitiesMap.has(entityKey)) {
                entitiesMap.set(entityKey, {
                    i: trade.entityId,
                    t: trade.entityType === 'Asset' ? 'a' : trade.entityType === 'Cryptocurrency' ? 'c' : 'i'
                });
            }
        });

        // Convert Map values to an array for the API call
        return Array.from(entitiesMap.values());
    }, [filteredTrades]);

    // Extract entities data
    const { entities, isLoading: entitiesLoading } = useFetchEntities(entitiesToFetch);

    // Extract options to fetch
    const optionsToFetch = useMemo(() => {
        if (!tradesQuery.data?.trades) return [];

        const optionsMap = new Map<string, EntityOptionObjectCompressed>();

        for (const trade of tradesQuery.data.trades) {
            for (const transaction of trade.transactions) {
                const optionId = transaction.derivativeId;
                const { derivativeType } = transaction;

                if (trade.entityType !== 'Asset' && trade.entityType !== 'AssetIndex') continue;
                if (!optionId || derivativeType !== 'Option') continue;

                const optionKey = `${optionId}_${trade.entityId}_${trade.entityType}`;

                if (!optionsMap.has(optionKey)) {
                    optionsMap.set(optionKey, {
                        oId: optionId,
                        eId: trade.entityId,
                        eType: trade.entityType
                    });
                }
            }
        }

        return Array.from(optionsMap.values());
    }, [tradesQuery.data]);

    const { options, isLoading: optionsLoading } = useFetchOptions(optionsToFetch);

    // Fetch trading requisites (strategies, Trade Logic V2 attachments, etc.)
    const { requisites, isLoading: requisitesLoading } = useTradingRequisites({
        errorConfig: {
            fallbackErrorMessage: 'Failed to fetch trading requisites',
            setError: (msg) => setError(msg)
        }
    });

    // Extract strategies and option buying strategies
    const strategies = useMemo(() => requisites?.tradingStrategies || [], [requisites?.tradingStrategies]);

    const optionBuyingStrategies = useMemo(() => requisites?.optionBuyingStrategies || [], [requisites?.optionBuyingStrategies]);

    // Calculate loading state
    const loading = useMemo(() => {
        return (
            backtestQuery.isLoading ||
            (backtest?.portfolioId && portfolioQuery.isLoading) ||
            tradesQuery.isLoading ||
            (entitiesToFetch.length > 0 && entitiesLoading) ||
            (optionsToFetch.length > 0 && optionsLoading) ||
            requisitesLoading
        );
    }, [
        backtestQuery.isLoading,
        backtest?.portfolioId,
        portfolioQuery.isLoading,
        tradesQuery.isLoading,
        entitiesToFetch.length,
        entitiesLoading,
        optionsToFetch.length,
        optionsLoading,
        requisitesLoading
    ]);

    useEffect(() => {
        if (portfolioQuery.error && backtest?.portfolioId) {
            handleTrpcQuery(portfolioQuery, {
                fallbackErrorMessage: 'Failed to fetch portfolio',
                setError: (msg) => setError(msg)
            });
        }
    }, [portfolioQuery.error, backtest?.portfolioId]);

    // Get current trades for pagination
    const getCurrentTrades = useMemo(() => {
        const indexOfLastTrade = currentPage * tradesPerPage;
        const indexOfFirstTrade = indexOfLastTrade - tradesPerPage;
        return filteredTrades.slice(indexOfFirstTrade, indexOfLastTrade);
    }, [currentPage, filteredTrades, tradesPerPage]);

    // Prepare chart data for trades
    const prepareChartData = useCallback(
        (trades: BacktestTrade[]) => {
            if (trades.length === 0) return [];

            try {
                // We already have strategies and option buying strategies from useTradingRequisites
                const entitiesToFetchMap: Map<
                    string,
                    {
                        et: 'Cryptocurrency' | 'Asset' | 'AssetIndex';
                        eId: number;
                        sdt: string;
                        edt: string;
                        d: BarDuration;
                        // Remove after hours
                        rah?: boolean | undefined;
                        // Allow partial bars
                        p?: boolean | undefined;
                    }
                > = new Map();

                for (const trade of trades) {
                    const entityKey = `${trade.entityId}_${trade.entityType}`;
                    // Find the strategy for this trade
                    const strategy = strategies.find((s) => s.id === trade.strategyId) || { duration: '30m' as BarDuration };

                    // Calculate start and end dates for chart data
                    // Get the first transaction timestamp
                    const firstTransactionTimestamp = trade.transactions[0]?.timestamp;
                    if (!firstTransactionTimestamp) continue;

                    // Get the last transaction timestamp
                    const lastTransactionTimestamp = trade.transactions[trade.transactions.length - 1]?.timestamp;
                    if (!lastTransactionTimestamp) continue;

                    // Calculate start date (14 days before first transaction)
                    const startDateTime = getDatePlusMinus('-', 14, 'day', 'ymdhms', null, firstTransactionTimestamp);

                    // Calculate end date (14 days after last transaction)
                    const endDateTime = getDatePlusMinus('+', 14, 'day', 'ymdhms', null, lastTransactionTimestamp);

                    if (!entitiesToFetchMap.has(entityKey)) {
                        entitiesToFetchMap.set(entityKey, {
                            eId: trade.entityId,
                            et: requireOrdinaryEntityType(trade.entityType),
                            sdt: startDateTime,
                            edt: endDateTime,
                            d: strategy.duration,
                            rah: false,
                            p: false
                        });
                    } else {
                        // If entity already exists, see if the start date is lower or end date it higher
                        const entity = entitiesToFetchMap.get(entityKey)!;

                        if (startDateTime < entity.sdt) {
                            entity.sdt = startDateTime;
                        }

                        if (endDateTime > entity.edt) {
                            entity.edt = endDateTime;
                        }
                    }
                }

                if (entitiesToFetchMap.size === 0) return [];

                return Array.from(entitiesToFetchMap.values());
            } catch (err) {
                console.error('Error preparing chart data:', err);
                setChartError('Failed to prepare chart data');
                showToast('Failed to prepare chart data', 'warning');
                return [];
            }
        },
        [strategies]
    );

    // Fetch bars data for entities
    const barsToFetch = useMemo(() => prepareChartData(getCurrentTrades), [getCurrentTrades, prepareChartData]);

    // Fetch bars data
    const barsQuery = useAppQuery(
        {
            ...trpc.bars.fetchBarsForDurationBundled.queryOptions({ entitiesToFetch: barsToFetch }),
            enabled: barsToFetch.length > 0
        },
        {
            fallbackErrorMessage: 'Failed to fetch chart data',
            setError: (msg) => setChartError(msg)
        }
    );

    const loadingCharts = barsQuery.isLoading;

    const chartData = useMemo<Record<string, BarOHLCVDetailed[]>>(() => {
        const entityBars = barsQuery.data?.entityBars ?? {};
        return Object.fromEntries(Object.entries(entityBars).map(([key, value]) => [key, value.bars]));
    }, [barsQuery.data?.entityBars]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Show loading spinner while data is loading (authentication is handled by UserLayout)
    if (loading) {
        return (
            <UserLayout title="Portfolio Backtest Trades">
                <div className="flex justify-center items-center min-h-[60vh]">
                    <DataLoadingSpinner text="Loading backtest trades..." />
                </div>
            </UserLayout>
        );
    }

    const displayError = portfolioBacktestId ? error : 'No portfolio backtest ID provided';

    if (displayError) {
        return (
            <UserLayout title="Portfolio Backtest Trades">
                <Alert variant="danger">{displayError}</Alert>
            </UserLayout>
        );
    }

    if (!backtest) {
        return (
            <UserLayout title="Portfolio Backtest Trades">
                <Alert variant="warning">No backtest data found</Alert>
            </UserLayout>
        );
    }

    const { id, name, startingBalance, endingBalance } = backtest;
    const profit = trimDecimals(endingBalance - startingBalance);
    const percentageGain = trimDecimals((profit / startingBalance) * 100);

    return (
        <UserLayout title="Portfolio Backtest Trades">
            <Card className="mb-4">
                <div className="p-4">
                    <h4 className="mb-3">Backtest Details</h4>
                    <DataTable
                        striped
                        columns={[
                            {
                                header: 'Backtest ID',
                                accessorKey: 'id',
                                minSize: 60,
                                size: 100,
                                maxSize: 120
                            },
                            {
                                header: 'Portfolio Name',
                                accessorKey: 'portfolioName',
                                minSize: 150,
                                size: 250,
                                maxSize: 350,
                                cell: (info) => {
                                    const portfolioData = info.row.original;
                                    return portfolioData.portfolioObj ? (
                                        <Link to={`/user/portfolio/${portfolioData.portfolioObj.id}`}>
                                            {portfolioData.portfolioObj.name}
                                        </Link>
                                    ) : (
                                        'Unknown'
                                    );
                                }
                            },
                            {
                                header: 'Backtest Name',
                                accessorKey: 'name',
                                minSize: 120,
                                size: 200,
                                maxSize: 300
                            },
                            {
                                header: 'Profit',
                                accessorKey: 'profit',
                                minSize: 80,
                                size: 125,
                                maxSize: 180,
                                cell: (info) => {
                                    const value = info.getValue() as number;
                                    return value > 0 ? (
                                        <span className="text-profit-green">${value}</span>
                                    ) : (
                                        <span className="text-red-500">${value}</span>
                                    );
                                }
                            },
                            {
                                header: 'Profit %',
                                accessorKey: 'profitPercentage',
                                minSize: 80,
                                size: 125,
                                maxSize: 180,
                                cell: (info) => {
                                    const value = info.getValue() as number;
                                    return value > 0 ? (
                                        <span className="text-profit-green">{value}%</span>
                                    ) : (
                                        <span className="text-red-500">{value}%</span>
                                    );
                                }
                            }
                        ]}
                        data={[
                            {
                                id,
                                portfolioName: portfolio ? portfolio.name : 'Unknown',
                                portfolioObj: portfolio,
                                name,
                                profit,
                                profitPercentage: percentageGain
                            }
                        ]}
                        search={false}
                    />
                </div>
            </Card>

            {filteredTrades.length === 0 ? (
                <Alert variant="info">No trades found for the selected filters</Alert>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-3">
                        <h4>Trades ({filteredTrades.length})</h4>
                        <div className="flex items-center gap-2">
                            <button
                                className="px-2 py-1 text-sm bg-zinc-700 hover:bg-zinc-600 text-white rounded mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                Previous
                            </button>
                            <span className="mr-2">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                className="px-2 py-1 text-sm bg-zinc-700 hover:bg-zinc-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>

                    <div className="space-y-5">
                        {getCurrentTrades.map((trade: BacktestTrade) => {
                            const entity = entities.find((e) => e.id === trade.entityId) || null;

                            const optionBuyingStrategiesForTrade: OptionBuyingStrategy[] = [];

                            trade.transactions.forEach((transaction: { optionBuyingStrategyId?: number | null }) => {
                                if (optionBuyingStrategiesForTrade.find((obs) => obs.id === transaction.optionBuyingStrategyId)) return;

                                if (transaction.optionBuyingStrategyId) {
                                    const foundStrategy = optionBuyingStrategies.find((s) => s.id === transaction.optionBuyingStrategyId);
                                    if (foundStrategy) optionBuyingStrategiesForTrade.push(foundStrategy);
                                }
                            });

                            return (
                                <div className="mb-5 w-full" key={trade.id}>
                                    <div className="w-full">
                                        <TradeHeader
                                            entity={entity}
                                            optionBuyingStrategies={optionBuyingStrategiesForTrade}
                                            options={options}
                                            strategy={strategies.find((s) => s.id === trade.strategyId) || null}
                                            trade={trade}
                                        />
                                    </div>

                                    {/* Trade Logic V2 exit summary */}
                                    <TradeLogicV2ExitSummary
                                        showCandidateInfo
                                        exitCandidateDisplay={trade.exitCandidateDisplay}
                                        exitCandidateHash={trade.exitCandidateHash}
                                        exitCandidateId={trade.exitCandidateId}
                                        strategy={strategies.find((s) => s.id === trade.strategyId)}
                                    />

                                    <Card>
                                        {loadingCharts ? (
                                            <div className="text-center py-4">
                                                <DataLoadingSpinner text="Loading chart data..." />
                                            </div>
                                        ) : chartError ? (
                                            <div className="text-center text-red-500 py-4">{chartError}</div>
                                        ) : chartData[`${trade.entityId}_${trade.entityType}`]?.length > 0 ? (
                                            <TradeChart
                                                showVolume
                                                bars={chartData[`${trade.entityId}_${trade.entityType}`]}
                                                duration={strategies.find((s) => s.id === trade.strategyId)?.duration || '30m'}
                                                entity={entity}
                                                trade={trade}
                                            />
                                        ) : (
                                            <div className="text-center text-zinc-400 py-4">
                                                No chart data available for this trade.
                                                <div className="mt-2">
                                                    <small className="text-zinc-500">
                                                        This could be because no bar data exists for this time period or entity.
                                                    </small>
                                                </div>
                                            </div>
                                        )}
                                    </Card>

                                    <Card className="mt-3">
                                        <h5 className="mb-3">Transactions</h5>
                                        <div className="px-4 pb-4">
                                            {renderTransactionOutput({
                                                transactions: trade.transactions,
                                                possibleOptions: new Map(
                                                    options.map((option) => [`${option.id}_${option.assetId}_${option.entityType}`, option])
                                                )
                                            })}
                                        </div>
                                    </Card>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex justify-center mt-4">
                        <nav aria-label="Portfolio backtest trades pagination" className="flex justify-center">
                            <ul className="flex space-x-1">
                                <li className={`${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <button
                                        className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-blue-400 hover:bg-blue-500 hover:text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageChange(1)}
                                    >
                                        First
                                    </button>
                                </li>
                                <li className={`${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <button
                                        className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-blue-400 hover:bg-blue-500 hover:text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {/* Show limited page numbers with ellipses when there are many pages */}
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter((page) => {
                                        // Always show first and last 2 pages
                                        if (page <= 2 || page > totalPages - 2) return true;
                                        // Always show current page and 1 page before and after
                                        if (Math.abs(page - currentPage) <= 1) return true;
                                        // Hide other pages
                                        return false;
                                    })
                                    .map((page, index, filteredPages) => {
                                        // Add ellipsis if there's a gap between consecutive pages
                                        const needsEllipsisBefore = index > 0 && page - filteredPages[index - 1] > 1;

                                        return (
                                            <React.Fragment key={page}>
                                                {needsEllipsisBefore && (
                                                    <li className="opacity-50 cursor-not-allowed">
                                                        <span className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-400 rounded">
                                                            …
                                                        </span>
                                                    </li>
                                                )}
                                                <li className={`${currentPage === page ? 'active' : ''}`}>
                                                    <button
                                                        className={`px-3 py-2 border border-zinc-700 rounded ${
                                                            currentPage === page
                                                                ? 'bg-blue-600 text-white'
                                                                : 'bg-zinc-800 text-blue-400 hover:bg-blue-500 hover:text-white'
                                                        }`}
                                                        onClick={() => handlePageChange(page)}
                                                    >
                                                        {page}
                                                    </button>
                                                </li>
                                            </React.Fragment>
                                        );
                                    })}
                                <li className={`${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <button
                                        className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-blue-400 hover:bg-blue-500 hover:text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    >
                                        Next
                                    </button>
                                </li>
                                <li className={`${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <button
                                        className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-blue-400 hover:bg-blue-500 hover:text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageChange(totalPages)}
                                    >
                                        Last
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </>
            )}
        </UserLayout>
    );
};

export default PortfolioBacktestTrades;
